<template>
  <div :data-widget-title="content.heroTitle">
    <div class="page-hero before:h-4/5 relative px-box-2 sm:px-box-4 pt-11 md:pt-28">
      <h1
        class="tagline relative title text-24 md:text-38 text-white lg:text-center mx-auto sm:w-136 md:w-152 lg:w-184"
      >
        {{ content.heroTitle }}
      </h1>

      <div class="box-container overflow-visible mt-14 md:mt-16 md:mx-auto md:w-168 lg:w-184 pb-8">
        <div class="text-center pt-8">
          <span class="text-14 font-semibold mt-1.5 block mb-4">{{
            $t('currency_chart.description')
          }}</span>

          <div class="flex justify-center items-center mb-4">
            <span class="block subtitle1 mr-4">
              1 {{ content.corridor.currencyFrom.iso }} = {{ formattedRate }}
              {{ content.corridor.currencyTo.iso }}
            </span>
            <button
              type="button"
              :class="[isFormVisible ? 'hidden' : 'flex']"
              class="p-2 bg-gray-50 rounded-8 md:hidden"
              @click="openCurrencyCalculator('edit')"
            >
              <svg role="presentation" focusable="false" tabindex="-1" class="icon icon-xs flex"
                 
                alt="change currencies"
                svg-inline
               xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16"><title>change currencies</title>
<path fill="#303030" d="M2 11.5V14h2.5l7.4-7.4-2.5-2.5L2 11.5Zm11.8-6.8c.3-.3.3-.7 0-1l-1.6-1.5a.7.7 0 0 0-.9 0l-1.2 1.2L12.6 6l1.2-1.2Z"/>
</svg>
            </button>
          </div>
          <ChartTrendMessage
            v-if="processedData && liveRate"
            class="flex justify-center items-center mb-4 text-14"
            :chart-data="processedData"
            :mid-market-rate="liveRate || 0"
          />
          <button
            type="button"
            :class="[isFormVisible ? 'hidden' : 'flex']"
            class="items-center mx-auto p-3 mt-2 bg-gray-50 rounded-8 sm:w-1/3 md:hidden"
            @click="openCurrencyCalculator('calculator')"
          >
            <svg role="presentation" focusable="false" tabindex="-1" class="icon icon-md"   alt="" svg-inline  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
<path fill="#303030" fill-rule="evenodd" d="M21.8 11.3h-9v-9H21a.8.8 0 0 1 .8.7v8.3Zm0 9.7a.8.8 0 0 1-.8.8h-8.3v-9h9V21Zm-10.6-9.8h-9V3a.8.8 0 0 1 .8-.8h8.3v9Zm0 10.6H3a.8.8 0 0 1-.8-.8v-8.3h9v9ZM21 .8H3C1.8.8.7 1.8.7 3v18c0 1.2 1 2.3 2.3 2.3h18c1.2 0 2.3-1 2.3-2.3V3c0-1.2-1-2.3-2.3-2.3Z" clip-rule="evenodd"/>
<path fill="#303030" fill-rule="evenodd" d="M18.8 6h-3a.8.8 0 1 0 0 1.5h3a.8.8 0 1 0 0-1.5Zm-3 13.5h3a.8.8 0 1 0 0-1.5h-3a.8.8 0 1 0 0 1.5Zm0-3h3a.8.8 0 1 0 0-1.5h-3a.8.8 0 1 0 0 1.5Zm-10.6-9H6v.8a.8.8 0 1 0 1.5 0v-.8h.8a.8.8 0 1 0 0-1.5h-.8v-.8a.8.8 0 1 0-1.5 0V6h-.8a.8.8 0 1 0 0 1.5Zm-.5 11.8a.8.8 0 0 0 1 0l1-1 1 1a.8.8 0 1 0 1-1l-.9-1 1-1a.8.8 0 1 0-1-1l-1 .9-1-1a.8.8 0 1 0-1 1l.9 1-1 1a.8.8 0 0 0 0 1Z" clip-rule="evenodd"/>
</svg>
            <span class="font-medium text-16 block ml-2"
              >{{ $t('hero_exchange_rate.calculator') }}
            </span>
          </button>
        </div>
        <ExchangeRateSearchForm
          class="px-4 pb-2 md:flex"
          :class="{ 'flex flex-col': isFormVisible, hidden: !isFormVisible }"
          :default-currency-from="content.corridor.currencyFrom.iso"
          :default-currency-to="content.corridor.currencyTo.iso"
          :default-amount="content.corridor.defaultAmount ?? 1000"
          :live-rate="liveRate"
        />
        <CurrencyChart
          class="my-8 px-2 sm:px-10"
          :currency-from="content.corridor.currencyFrom.iso"
          :currency-to="content.corridor.currencyTo.iso"
          :mid-market-rate="liveRate"
          @chart-data="processChartData"
          size="small"
        />
        <div class="mx-4 md:mx-auto">
          <a
            class="btn btn-md btn-amber w-full md:w-1/2 md:mx-auto block"
            href="#imt-search-modal"
            @click="openModal"
          >
            {{ $t('hero_exchange_rate.get_best_rate') }}
          </a>
        </div>
      </div>
    </div>
    <SponsorshipCard
      class="w-full md:w-1/2 mx-auto"
      :currency-from="content.corridor.currencyFrom"
      :currency-to="content.corridor.currencyTo"
    />
  </div>
</template>

<script lang="ts" setup>
import ExchangeRateSearchForm from './exchange-rate-search-form.vue'
import SponsorshipCard from './sponsorship-card.vue'
import type { GQLHeroExchangeRateSlice } from './generated/hero-exchange-rate'
import type { ChartData } from 'chart.js'
import {
  HeroExchangeRateModalToggle,
  CurrencyCalculatorOpen,
} from '@core/analytics/core-events/heroExchangeRate'

const props = defineProps<{ content: GQLHeroExchangeRateSlice }>()

const { $lang, $t, $track } = useNuxtApp()

const isFormVisible = ref(false)
async function openCurrencyCalculator(type: 'edit' | 'calculator') {
  isFormVisible.value = true
  await $track(CurrencyCalculatorOpen({ buttonType: type }))
}

async function openModal() {
  await $track(HeroExchangeRateModalToggle(true))
}

const processedData = ref<Record<string, ChartData> | null>(null)

const liveRate = useLiveExchangeRate(
  props.content.corridor.currencyFrom.iso,
  props.content.corridor.currencyTo.iso,
)

const formattedRate = computed(() => {
  return liveRate.value ? formatRate(liveRate.value, $lang) : undefined
})

function processChartData(chartData: Record<string, ChartData> | null) {
  if (chartData && changedIn2months(chartData)) {
    processedData.value = chartData
  }
}
</script>
